import styled, { css } from "styled-components";
import { Button, TextField } from "@mui/material";
import { palette, typography } from "@src/core/styles";

export const ToolContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1400px;
`;

export const ToolIntro = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 920px;
`;

export const ToolTitle = styled.h1`
  ${typography.display_small};
  margin: 0;
  color: ${palette.colors.onsurface};
`;

export const ToolSubtitle = styled.p`
  ${typography.body_large_regular};
  color: ${palette.colors.onsurface_variant};
  margin: 0;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

export const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ToolLabel = styled.span`
  ${typography.body_medium_regular};
  color: ${palette.colors.onsurface_variant};
  white-space: nowrap;
`;

export const Select = styled.select`
  ${typography.body_medium_regular};
  padding: 6px 10px;
  border: 1px solid ${palette.colors.outline_variant};
  border-radius: 8px;
  background-color: ${palette.colors.surface};
  color: ${palette.colors.onsurface};
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${palette.colors.green_base};
  }
`;

export const NumberInput = styled.input`
  ${typography.body_medium_regular};
  padding: 6px 10px;
  border: 1px solid ${palette.colors.outline_variant};
  border-radius: 8px;
  background-color: ${palette.colors.surface};
  color: ${palette.colors.onsurface};
  width: 80px;
  outline: none;

  &:focus {
    border-color: ${palette.colors.green_base};
  }
`;

export const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${palette.colors.outline_variant};
  border-radius: 12px;
  overflow: hidden;
  background-color: ${palette.colors.surface};
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: ${palette.colors.surface_container};
  border-bottom: 1px solid ${palette.colors.outline_variant};
  min-height: 44px;
  gap: 8px;
  flex-wrap: wrap;
`;

export const PanelTitle = styled.span`
  ${typography.body_medium_regular};
  font-weight: 500;
  color: ${palette.colors.onsurface_variant};
`;

export const PanelActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const PanelBody = styled.div<{ $padding?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $padding }) => ($padding ? "padding: 14px;" : "")}
  gap: 10px;
`;

export const ActionButton = styled(Button)`
  && {
    ${typography.body_large_medium};
    background-color: ${palette.colors.green_base};
    color: ${palette.colors.green10};
    padding: 10px 20px;
    border-radius: 8px;
    text-transform: none;
    min-width: 130px;

    &:hover {
      background-color: ${palette.colors.button_hover};
    }

    &:disabled {
      background-color: ${palette.colors.outline_variant};
      color: ${palette.colors.onsurface_variant};
    }

    &.MuiButton-outlined {
      background-color: transparent;
      border-color: ${palette.colors.green_base};
      color: ${palette.colors.green_base};

      &:hover {
        background-color: ${palette.colors.surface_container};
        border-color: ${palette.colors.green60};
      }

      &:disabled {
        border-color: ${palette.colors.outline_variant};
        color: ${palette.colors.onsurface_variant};
      }
    }
  }
`;

export const SmallButton = styled(Button)`
  && {
    ${typography.body_medium_regular};
    font-size: 13px;
    border-color: ${palette.colors.outline_variant};
    color: ${palette.colors.onsurface_variant};
    text-transform: none;
    padding: 4px 10px;
    min-width: auto;

    &:hover {
      background-color: ${palette.colors.surface_container};
      border-color: ${palette.colors.green_base};
      color: ${palette.colors.green_base};
    }
  }
`;

export const ErrorMessage = styled.div`
  ${typography.body_medium_regular};
  color: ${palette.colors.error};
  padding: 10px 14px;
  background-color: ${palette.colors.surface};
  border-top: 1px solid ${palette.colors.error};
  font-family: "JetBrains Mono", "Fira Code", "Courier New", monospace;
  font-size: 13px;
`;

export const SuccessMessage = styled.div`
  ${typography.body_medium_regular};
  color: ${palette.colors.green_base};
  padding: 10px 14px;
  background-color: ${palette.colors.secondary_container};
  border-top: 1px solid ${palette.colors.green_base};
`;

export const MonoArea = styled(TextField)`
  && {
    width: 100%;

    textarea,
    input {
      ${typography.body_medium_regular};
      font-family: "JetBrains Mono", "Fira Code", "Courier New", "Monaco",
        monospace;
      font-size: 13px;
      line-height: 1.5;
      color: ${palette.colors.onsurface};
    }

    .MuiOutlinedInput-root {
      border-radius: 0;
      background-color: ${palette.colors.surface};
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  }
`;

export const InfoGrid = styled.div`
  display: flex;
  gap: 24px;
  padding: 14px 20px;
  background-color: ${palette.colors.surface_container};
  border-radius: 10px;
  border: 1px solid ${palette.colors.outline_variant};
  flex-wrap: wrap;
`;

export const InfoItem = styled.div`
  ${typography.body_medium_regular};
  color: ${palette.colors.onsurface_variant};

  strong {
    color: ${palette.colors.onsurface};
    margin-right: 6px;
  }
`;

export const StatusBadge = styled.span<{ $variant?: "ok" | "warn" | "err" }>`
  ${typography.label_small_medium};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  ${({ $variant = "ok" }) => {
    if ($variant === "err") {
      return css`
        background-color: #fde6e4;
        color: ${palette.colors.error};
      `;
    }
    if ($variant === "warn") {
      return css`
        background-color: #fff4cc;
        color: #8a6d00;
      `;
    }
    return css`
      background-color: ${palette.colors.secondary_container};
      color: ${palette.colors.green_base};
    `;
  }}
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionTitle = styled.h2`
  ${typography.headline_small};
  margin: 0;
  color: ${palette.colors.onsurface};
`;

export const ArticleBody = styled.div`
  ${typography.body_large_regular};
  color: ${palette.colors.onsurface_variant};
  max-width: 920px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  h2,
  h3 {
    margin: 12px 0 0;
    color: ${palette.colors.onsurface};
  }

  h2 {
    ${typography.headline_small};
  }

  h3 {
    ${typography.title_medium};
  }

  p {
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  code {
    font-family: "JetBrains Mono", "Fira Code", "Courier New", monospace;
    background-color: ${palette.colors.surface_container};
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
    color: ${palette.colors.onsurface};
  }

  a {
    color: ${palette.colors.link};
  }
`;

export const Chip = styled.button<{ $active?: boolean }>`
  ${typography.body_medium_regular};
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${palette.colors.outline_variant};
  background-color: ${({ $active }) =>
    $active ? palette.colors.secondary_container : palette.colors.surface};
  color: ${({ $active }) =>
    $active ? palette.colors.green_base : palette.colors.onsurface_variant};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${palette.colors.green_base};
  }
`;

export const Tabs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ResultBox = styled.pre`
  ${typography.body_medium_regular};
  font-family: "JetBrains Mono", "Fira Code", "Courier New", monospace;
  font-size: 13px;
  margin: 0;
  padding: 14px;
  background-color: ${palette.colors.surface};
  color: ${palette.colors.onsurface};
  white-space: pre-wrap;
  word-break: break-all;
  overflow: auto;
  min-height: 60px;
`;
